import { getInvestmentYearEnd, getInvestmentYearStart } from "../home/shared";

const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

// calculateAnnualInterest raises growth to the power of 1/years, so over a very
// short history it compounds a small move into an absurd rate (a 3% gain over
// four days annualises into the thousands of percent). Clamping keeps
// projections finite and stops one lucky week from erasing the
// required-contribution figure entirely.
export const RATE_CLAMP = { min: -90, max: 100 };

export const clampAnnualPercent = (annualPercent) => {
	const value = Number(annualPercent);
	if (!Number.isFinite(value)) return 0;
	return Math.min(RATE_CLAMP.max, Math.max(RATE_CLAMP.min, value));
};

// Geometric, to match the compounding used by projectFutureValue below.
export const toMonthlyRate = (annualPercent) => {
	const annual = clampAnnualPercent(annualPercent) / 100;
	if (annual <= -1) return -1;
	return Math.pow(1 + annual, 1 / 12) - 1;
};

export const monthsBetween = (from, to) => {
	const start = new Date(from).getTime();
	const end = new Date(to).getTime();
	if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
	return Math.max(0, (end - start) / MS_PER_MONTH);
};

// FV = PV(1+r)^n + PMT * [((1+r)^n - 1) / r]
// Ordinary annuity: contributions land at the END of each month. Investing at
// the start of each month would lower the requirement by a factor of (1+r).
export const projectFutureValue = ({
	currentWorth = 0,
	monthlyRate = 0,
	months = 0,
	monthlyContribution = 0,
} = {}) => {
	if (months <= 0) return currentWorth;
	if (monthlyRate <= -1) {
		return Math.max(0, currentWorth + monthlyContribution * months);
	}

	const growth = Math.pow(1 + monthlyRate, months);
	const annuityFactor = monthlyRate === 0 ? months : (growth - 1) / monthlyRate;

	return currentWorth * growth + monthlyContribution * annuityFactor;
};

// Solve the above for PMT given a target FV.
export const requiredMonthlyContribution = ({
	currentWorth = 0,
	target = 0,
	monthlyRate = 0,
	months = 0,
} = {}) => {
	if (!(target > 0)) return 0;

	// Deadline reached or passed: only a lump sum closes the gap.
	if (months <= 0) return Math.max(0, target - currentWorth);

	// Total loss; compounding is meaningless, split the gap evenly.
	if (monthlyRate <= -1 || !Number.isFinite(monthlyRate)) {
		return Math.max(0, (target - currentWorth) / months);
	}

	const growth = Math.pow(1 + monthlyRate, months);
	const projected = currentWorth * growth;
	// Positive for any rate > -1, so negative-return cases still use the real
	// formula rather than falling back to a linear split.
	const annuityFactor = monthlyRate === 0 ? months : (growth - 1) / monthlyRate;

	if (!Number.isFinite(annuityFactor) || annuityFactor <= 0) {
		return Math.max(0, (target - currentWorth) / months);
	}

	return Math.max(0, (target - projected) / annuityFactor);
};

// Both goal types are due at the end of an investment year: a FINAL goal for
// 2035 means by 11 December 2035. One calendar, no special case.
export const buildGoalProgress = ({
	goal,
	currentWorth = 0,
	annualPercent = 0,
	now = new Date(),
} = {}) => {
	const target = Number(goal?.amount || 0);
	const periodStart = getInvestmentYearStart(goal?.year);
	const deadline = getInvestmentYearEnd(goal?.year);
	const clampedAnnual = clampAnnualPercent(annualPercent);
	const monthlyRate = toMonthlyRate(clampedAnnual);
	const monthsRemaining = monthsBetween(now, deadline);

	const projectedValue = projectFutureValue({
		currentWorth,
		monthlyRate,
		months: monthsRemaining,
	});
	const requiredMonthly = requiredMonthlyContribution({
		currentWorth,
		target,
		monthlyRate,
		months: monthsRemaining,
	});

	const progressPercent = target > 0 ? (currentWorth / target) * 100 : 0;
	const projectedPercent = target > 0 ? (projectedValue / target) * 100 : 0;
	const achieved = target > 0 && currentWorth >= target;
	const expired = now > deadline;

	return {
		goal,
		target,
		currentWorth,
		periodStart,
		deadline,
		monthsRemaining,
		annualPercent: clampedAnnual,
		monthlyRatePercent: monthlyRate * 100,
		projectedValue,
		gap: Math.max(0, target - currentWorth),
		projectedGap: Math.max(0, target - projectedValue),
		surplus: Math.max(0, projectedValue - target),
		requiredMonthly,
		onTrack: requiredMonthly <= 0,
		progressPercent,
		projectedPercent,
		// Progress renders translateX(-(100 - value)%), so anything over 100
		// pushes the bar off-screen -- it would vanish exactly when you're ahead.
		barValue: Math.max(0, Math.min(100, progressPercent)),
		// EXPIRED outranks ACHIEVED on purpose. `currentWorth` is always today's
		// value, so for a goal whose deadline has passed, comparing it to the
		// target says nothing about whether the goal was met at the time -- it
		// would label any old goal "Achieved" purely because the portfolio has
		// grown since. Flagging the deadline is the honest read.
		status: expired
			? "EXPIRED"
			: achieved
			? "ACHIEVED"
			: requiredMonthly <= 0
			? "ON_TRACK"
			: "BEHIND",
	};
};

// A goal is solved twice, because "what do I need to invest?" has two honest
// answers. Compounding the measured return forward assumes an exceptional past
// repeats for a decade; compounding a plain long-run rate ignores how this
// portfolio has actually done. Showing one without the other hides the
// assumption that decides the number.
//
// 12%/yr is an assumption, not a measurement: a plain long-run equity estimate,
// kept here as the single place to change it.
export const BASELINE_ANNUAL_PERCENT = 12;

// The baseline is the primary scenario -- status, the projected segment of the
// progress bar and the headline contribution all come from it -- so nothing on
// the page reports a goal as reached on the strength of a past that may not
// repeat. The measured return rides along in `scenarios` for comparison.
export const buildGoalScenarios = ({
	goal,
	currentWorth = 0,
	historicalPercent = 0,
	baselinePercent = BASELINE_ANNUAL_PERCENT,
	now = new Date(),
} = {}) => {
	const baseline = buildGoalProgress({
		goal,
		currentWorth,
		annualPercent: baselinePercent,
		now,
	});
	const historical = buildGoalProgress({
		goal,
		currentWorth,
		annualPercent: historicalPercent,
		now,
	});

	return { ...baseline, scenarios: { baseline, historical } };
};
